<template>
    <fieldset aria-describedby="content-info-text">
        <legend
            :id="uniqueId"
            class="legendelement"
        >
            {{ searchFieldLabel }}
        </legend>
        <div class="search-container">
            <div class="search-field-container">
                <div class="input-group search-group">
                    <textarea
                        id="customSearch"
                        ref="addressInput"
                        v-model="address"
                        role="combobox"
                        aria-autocomplete="both"
                        :aria-expanded="isListOpen"
                        aria-controls="searchResultList"
                        :aria-activedescendant="focusedOptionId"
                        class="search custom-search"
                        :placeholder="searchPlaceholder"
                        :aria-labelledby="uniqueId"
                        @keydown.enter.prevent="flyToCoordinates(undefined, $event)"
                        @keydown.down.prevent="focusNextOption"
                        @keydown.up.prevent="focusPreviousOption"
                        @input="checkInput"
                    />
                    <button
                        v-if="searchAddressSelected != null"
                        class="closebutton"
                        :aria-label="crossIconAlt"
                        @click.prevent="emptyResults"
                    >
                        <img
                            class="close-icon"
                            :src="crossIcon"
                            :alt="crossIconAlt"
                            aria-hidden="true"
                        >
                    </button>
                    <input
                        class="searchbutton"
                        type="button"
                        role="button"
                        :aria-label="searchLabel"
                        @click.prevent="searchByAddressClick"
                    >
                </div>
            </div>

            <div class="search-results">
                <div
                    v-if="noResultsMessage"
                    class="result-header search-not-found error-message"
                    role="alert"
                    :aria-labelledby="uniqueId"
                >
                    {{ $t("App.SearchAddress.NoResults") }}
                </div>
                <div
                    v-if="searchResults.length > 0"
                    class="result-header"
                >
                    {{ searchResults.length }} {{ $t("App.SearchAddress.Header.Targets") }}
                </div>
                <div
                    v-if="searchResults.length > 0"
                    class="search-results-list"
                    tabindex="-1"
                >
                    <ul
                        id="searchResultList"
                        role="listbox"
                    >
                        <li
                            v-for="(result, index) in searchResults"
                            :id="'option-' + index"
                            :key="index"
                            ref="addressListItems"
                            role="option"
                            class="search-row"
                            :aria-selected="index === activeIndex"
                            @click.prevent="flyToCoordinates(result)"
                        >
                            {{ result.getShortName() }}
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    </fieldset>
</template>

<script lang="ts">
    import { Component, Vue, Watch, Prop } from "vue-facing-decorator"
    import ApiService from "../services/ApiService"
    import SearchResultModel from "../models/searchresultmodel"
    import LatLngPoint from "../models/latlngpoint"
    import proj4 from "proj4"
    import useEventsBus from '../eventBus'
    import { store } from '@/store/store'

    const { bus } = useEventsBus()

    @Component
    export default class SearchAddress extends Vue {
        @Prop() label: string | undefined
        @Prop() placeholder: string | undefined
        apiService: ApiService = new ApiService(store.getters.baseHref)
        address: string = ""
        searchResults: Array<SearchResultModel> = []
        noResultsMessage: boolean = false
        uniqueId: string = ""
        pageNameRadiomikrofonitaajuudet: string = "Radiomikrofonitaajuudet"
        timeoutId: number | null = null
        activeIndex: number = -1

        created() {
            this.uniqueId = "id-" + Math.random().toString(16).slice(2)
        }

        activated() {
            // SearchAddress komponentteja jää eloon toisille välilehdille esim. liikenne vs viestintä. Ja sitten kaikki kuuntelevat
            // Eventtejä joiden perusteella sisällöt päivittyy. Tyhjätään aktivoidessa komponetti niin on tyhjä, vaikka olisikin saanut
            // jonkin eventin ollessa piilossa.
            this.emptyResults()
        }

        get isListOpen(): string {
            if (this.searchResults.length > 0) {
                return "true"
            }
            else {
                return "false"
            }
        }

        get searchAddressSelected(): SearchResultModel | null {
            return store.getters.searchAddressSelected
        }

        @Watch("searchAddressSelected")
        async onSearchAddressSelected(): Promise<void> {
            if (this.searchAddressSelected != null && this.searchAddressSelected.label != null) {
                this.address = this.searchAddressSelected.getShortName()
            }
        }

        @Watch('addressInputFocus')
        async onAddressInputFocus(): Promise<void> {
            this.$nextTick(() => {
                (this.$refs.addressInput as HTMLTextAreaElement).focus()
            })
        }

        get addressInputFocus() {
            return bus.value.get('addressInputFocus')
        }

        get searchLabel(): string | undefined {
            return this.$t("App.SearchButton.Label")
        }

        get crossIcon(): string {
            return store.getters.contentServiceUrl + "/Content/icons/svg-blue/ruksi.svg" + "?" + store.getters.contentServiceTimeStamp
        }

        get crossIconAlt(): string | undefined {
            return this.$t("App.SearchAddress.ClearSearchfield")
        }

        get searchPlaceholder(): string | undefined {
            if (!this.placeholder) {
                return this.$t("App.SearchAddress.InputPlaceholder")
            }
            return this.placeholder
        }

        get userLang(): string {
            return store.getters.activeLang
        }

        get coordinates(): LatLngPoint {
            return store.getters.coordinates
        }

        get searchFieldLabel() {
            if (!this.label) {
                return this.$t("App.SearchAddress.Heading.EnterLocation")
            }
            return this.label
        }

        get focusedOptionId() {
            return "option-" + this.activeIndex
        }

        @Watch("coordinates")
        async onCoordinatesChanged(): Promise<void> {
            // Tyhjennetään 'Kirjoita osoite' kenttä kun 'Paikanna minut' -toimintoa käytetään
            this.address = ""
            this.noResultsMessage = false
            this.checkInput()
        }

        get activeLang(): SearchResultModel {
            return store.getters.activeLang
        }

        @Watch("activeLang")
        async onActiveLang(): Promise<void> {
            // Tyhjennetään osoitekenttä ja hakutulokset kielenvaihdon yhteydessä
            this.emptyResults()
        }

        async searchByAddressClick(): Promise<void> {
            if (this.address && this.address.startsWith('E='))
                return
            // asetetaan noResultsMessage boolean falseksi kun haku käynnistyy, jotta
            // ruudunlukija lukee tekstin uudelleen, jos peräkkäin tulee useampia nollatuloksia
            this.noResultsMessage = false
            if (this.address) {
                store.dispatch("setSearchAddressSelected", null)
                store.dispatch("setRightSlotData", "")
                store.dispatch("setLoading", { name: "searchByAddress", value: true })
                await this.apiService.getGeocodingAddress(this.address, this.userLang).then(async (result) => {
                    const featureCollection = result.features
                    await this.featureCollectionToSearchResults(featureCollection)
                }).finally(() => {
                    store.dispatch("setLoading", { name: "searchByAddress", value: false })
                })
            }
        }

        async featureCollectionToSearchResults(collection): Promise<void> {
            const results: Array<SearchResultModel> = []
            if (collection.length === 0 && this.address !== "") {
                this.noResultsMessage = true
                this.searchResults = []
            }
            else if (this.timeoutId === null && this.address === "") {
                this.noResultsMessage = false
                this.searchResults = []
            }
            else {
                this.noResultsMessage = false
                for (let i = 0; i < collection.length; i++) {
                    const singleResult = collection[i]
                    const singleData: SearchResultModel = new SearchResultModel(
                        singleResult.properties["label:region"],
                        singleResult.properties["label:subregion"],
                        singleResult.properties["label:municipality"],
                        singleResult.properties["label:placeType"],
                        singleResult.properties.label,
                        new LatLngPoint(0, 0) // Alustetaan koordinaatit tyhjin� ja projisoidaan ne my�hemmin
                    )

                    if (!results.some(e => e.getShortName() === singleData.getShortName())) {
                        const coors = this.projectCoordinates(singleResult.geometry.coordinates)
                        singleData.coordinates = new LatLngPoint(coors[1], coors[0])

                        if (store.getters.currentPage === "Radiomikrofonitaajuudet") {
                            store.dispatch("setFrequencySearchData", {
                                lat: coors[1],
                                lng: coors[0],
                                f1: store.getters.frequencySearchData.f1,
                                f2: store.getters.frequencySearchData.f2,
                                bw: store.getters.frequencySearchData.bw
                            })
                        }

                        results.push(singleData)
                    }
                }

                // hakutulokset aakkosjärjestykseen
                this.sortSearchResults(results)

                this.searchResults = results
            }
        }

        flyToCoordinates(result?: SearchResultModel, event?: KeyboardEvent): void {
            if (event && event.key === "Enter" && this.activeIndex !== -1) {
                store.dispatch("setSearchAddressSelected", this.searchResults[this.activeIndex] as SearchResultModel)
                this.searchResults = []
            }
            else if (result) {
                store.dispatch("setSearchAddressSelected", result)
                this.searchResults = []
            }
            this.activeIndex = -1
        }

        projectCoordinates(coordinates): void {
            return proj4("EPSG:3067", "EPSG:4326", coordinates)
        }

        emptyResults(): void {
            this.noResultsMessage = false
            this.searchResults = []
            store.dispatch("setSearchAddressSelected", null)
            this.address = ""
            // tyhjennetään oikeasta palstasta Radiomikrofonitaajuudet sivulla näytettävät taajuudet
            if (store.getters.currentPage === this.pageNameRadiomikrofonitaajuudet) {
                store.dispatch("setRightSlotData", "")
                store.dispatch("setFrequencySearchData", {
                    lat: 0,
                    lng: 0,
                    f1: store.getters.frequencySearchData.f1,
                    f2: store.getters.frequencySearchData.f2,
                    bw: store.getters.frequencySearchData.bw
                })
            }
        }

        sortSearchResults(results: SearchResultModel[]): void {
            results.sort(function (a: SearchResultModel, b: SearchResultModel) {
                const labelA = a.label.toUpperCase()
                const labelB = b.label.toUpperCase()
                return (labelA < labelB) ? -1 : (labelA > labelB) ? 1 : 0
            })
        }

        scrollToActiveItem() {
            const listItems = this.$refs.addressListItems as HTMLElement

            // Scroll the highlighted item into view
            if (this.activeIndex > -1 && listItems[this.activeIndex]) {
                listItems[this.activeIndex].scrollIntoView({
                    block: "nearest",
                    inline: "nearest"
                })
            }
        }

        focusNextOption() {
            if (this.searchResults.length > 0) {
                this.activeIndex = (this.activeIndex + 1) % this.searchResults.length
                this.scrollToActiveItem()
            }
        }

        focusPreviousOption() {
            if (this.searchResults.length > 0) {
                this.activeIndex = (this.activeIndex - 1 + this.searchResults.length) % this.searchResults.length
                this.scrollToActiveItem()
            }
        }

        debounceFetch() {
            // Mikäli käyttäjä lakkaa kirjoittamasta sijainti-kenttään 500 millisekunniksi,
            // laukaistaan osoitehaku rajapinnasta, jolloin vaihtoehdot päivittyvät näkyviin
            if (this.timeoutId !== null) {
                clearTimeout(this.timeoutId)
                this.timeoutId = null
            }
            this.timeoutId = window.setTimeout(() => {
                this.searchByAddressClick()
            }, 500)
        }

        checkInput() {
            // Asetetaan this.activeIndex = -1, jotta näppäimistöllä selaus alkaa taas alusta kun hakutuloslista seuraavan kerran ilmestyy
            this.activeIndex = -1
            if (!this.address) {
                if (this.timeoutId !== null) {
                    clearTimeout(this.timeoutId)
                    this.timeoutId = null
                }
                this.emptyResults()
            }
            else {
                this.debounceFetch()
            }
        }
    }
</script>
<style scoped>

    .legendelement {
        font-size: 1.125rem;
        font-family: Montserrat;
    }

    .search-results {
        position: relative;
        width: 100%;
        z-index: 5;
        background-color: white;
    }

    .search-results-list {
        overflow-y: auto;
        z-index: 5;
        background-color: white;
        top: 40px;
        max-height: 300px;
        width: 100%;
        padding-left: 15px;
    }

    .result-header {
        padding: 10px 0px;
        padding-left: 10px;
        background-color: white;
        width: 100%;
    }

    .search-row {
        border-top: 1px solid #D3D3D3;
        border-bottom: 1px solid #ffffff;
        padding: 5px 0px;
        cursor: pointer;
    }

        .search-row:hover {
            background-color: #F2F4F8;
        }

    .search-municipality {
        color: var(--Traficom-main-color);
    }

    .search-group textarea.custom-search {
        width: unset;
        flex-grow: 1;
        height: 3.125rem;
        overflow-y: auto;
        padding-right: 2.1rem;
        resize: none;
    }

    .searchbutton:focus {
        outline: none;
        box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.75), 0 0 0 4px #002C74;
    }

    .closebutton {
        background: none;
        border: none;
        margin: 0;
        padding: 0;
        cursor: pointer;
        font-weight: 600;
        position: absolute;
        right: 70px;
        top: 12px;
    }

    ul {
        list-style-type: none;
        padding-left: 0;
        margin: 0;
    }

    [role="option"][aria-selected="true"],
    [role="listbox"] [role="option"]:hover {
        background-color: #def;
        border-top: 1px solid #1C1C1C;
        border-bottom: 1px solid #1C1C1C;
    }
</style>
